import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Language } from "../../../utils";
import makeClasses from "../styles";
import Grid from "@material-ui/core/Grid";
import { InputLabel } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { user as userActions } from "../../../actions";
import { toast } from "react-toastify";
import clsx from "clsx";
import { Button, ErrorIcon } from "../../../components";

const StepFour = ({
  history,
  nextStep,
  prevStep,
  handleChange,
  stepOneData,
}) => {
  const dispatch = useDispatch();
  const classes = makeClasses();
  const [inputPhone, setInputPhone] = useState("");
  const [inputPhoneConfirmation, setInputPhoneConfirmation] = useState("");
  const [hasPhoneMismatchError, setHasPhoneMismatchError] = useState(false);
  const [countries, setCountries] = useState([]);
  const [inputPhoneCharacteristic, setInputPhoneCharacteristic] =
    useState(null);

  const canMoveToNext =
    inputPhone.trim().length > 0 &&
    inputPhoneConfirmation.trim().length > 0 &&
    !hasPhoneMismatchError &&
    inputPhone === inputPhoneConfirmation &&
    inputPhoneCharacteristic !== null;

  useEffect(() => {
    dispatch(
      userActions.getCountries((countries) => {
        setCountries(countries);
        countries.forEach((country) => {
          if (country.alphaCode2 === "UY") {
            setInputPhoneCharacteristic(country.id);
          }
        });
      }),
    );
  }, [dispatch]);

  const { loading, languageTexts } = useSelector((state) => ({
    loading: state.user.loading,
    languageTexts: state.language.texts || {},
  }));

  const i18n = Language(languageTexts);

  const _getOptionLabel = (array) => (option) => {
    let foundOption = null;
    if (array === "countries") {
      foundOption = countries.find((c) => c.id === option);
      if (foundOption) return foundOption.name + " " + foundOption.dialingCode;
    } else if (array === "phoneCharacteristics") {
      foundOption = countries.find((c) => c.id === option);
      if (foundOption)
        return foundOption.name + " (" + foundOption.dialingCode + ")";
    }
    if (foundOption) {
      return foundOption.name;
    }
    return "";
  };

  const _handleOnChangePhoneCharacteristic = (e, newValue) => {
    setInputPhoneCharacteristic(newValue);
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    if (value !== "" && !/^\d+$/.test(value)) return;
    setInputPhone(value);
    setHasPhoneMismatchError(
      inputPhoneConfirmation.length > 0 && value.trim() !== inputPhoneConfirmation.trim(),
    );
  };

  const handlePhoneConfirmationChange = (e) => {
    const value = e.target.value;
    if (value !== "" && !/^\d+$/.test(value)) return;
    setInputPhoneConfirmation(value);
    setHasPhoneMismatchError(value.trim() !== inputPhone.trim());
  };

  const handleNext = () => {
    const phone = inputPhone.trim();
    const phoneConfirmation = inputPhoneConfirmation.trim();

    if (phone !== phoneConfirmation) {
      setHasPhoneMismatchError(true);
      return;
    }

    nextStep({ phone: phone, countryId: inputPhoneCharacteristic });
  };

  return (
    <React.Fragment>
      <div className={classes.formContent}>
        <div className={classes.formTitleContainer}>
          <div className={classes.formTitle}>
            {i18n.get("LegalEntityRegistration.StepFormTitle")}
          </div>
          <div className={classes.formText}>
            {i18n.get("LegalEntityRegistration.StepFourFormText")}
          </div>
        </div>
        <form layout="vertical" onSubmit={handleNext} className={classes.form}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <InputLabel className={classes.formLabel}>
                {i18n.get("LegalEntityRegistration.InputPhoneCountryLabel")}
              </InputLabel>
              <Autocomplete
                options={countries.map((c) => c.id)}
                getOptionLabel={_getOptionLabel("phoneCharacteristics")}
                onChange={_handleOnChangePhoneCharacteristic}
                classes={{ inputRoot: classes.formInputSelect }}
                value={inputPhoneCharacteristic}
                renderInput={(params) => (
                  <TextField
                    className={classes.formInput}
                    classes={{ root: classes.formInput }}
                    variant="outlined"
                    placeholder={i18n.get(
                      "LegalEntityRegistration.InputPhoneCharacteristic",
                    )}
                    {...params}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel className={classes.formLabel}>
                {i18n.get("LegalEntityRegistration.InputPhoneNumberLabel")}
              </InputLabel>
              <TextField
                placeholder={i18n.get(
                  "LegalEntityRegistration.InputPhoneNumberPlaceholder",
                )}
                className={classes.formInput}
                onChange={handlePhoneChange}
                classes={{ root: classes.formInput }}
                variant="outlined"
                value={inputPhone}
                inputProps={{
                  inputMode: "numeric",
                  autoComplete: "off",
                  onCopy: (e) => e.preventDefault(),
                  onPaste: (e) => e.preventDefault(),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel className={classes.formLabel}>
                {i18n.get(
                  "LegalEntityRegistration.InputPhoneConfirmationLabel",
                )}
              </InputLabel>
              <TextField
                error={hasPhoneMismatchError}
                placeholder={i18n.get(
                  "LegalEntityRegistration.InputPhoneConfirmationPlaceholder",
                )}
                className={classes.formInput}
                onChange={handlePhoneConfirmationChange}
                classes={{ root: classes.formInput }}
                variant="outlined"
                value={inputPhoneConfirmation}
                inputProps={{
                  inputMode: "numeric",
                  autoComplete: "off",
                  onCopy: (e) => e.preventDefault(),
                  onPaste: (e) => e.preventDefault(),
                }}
              />
              {hasPhoneMismatchError && (
                <div className={classes.errorMessageContainer}>
                  <ErrorIcon />
                  <div className={classes.errorMessage}>
                    {i18n.get("LegalEntityRegistration.InputPhoneMismatchError")}
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </form>
      </div>
      <div className={classes.termsWrapper}>
        <div
          className={clsx(
            classes.formSubmitContainer,
            classes.formSubmitContainerWithOneButton,
            classes.formSubmitContainerWithNoMargin,
          )}>
          <Button
            className={classes.formSubmitButton}
            disabled={loading || !canMoveToNext}
            onClick={handleNext}>
            {i18n.get("LegalEntityRegistration.NextButton")}
          </Button>
        </div>
      </div>
    </React.Fragment>
  );
};
StepFour.id = "com.Handy.LegalEntityRegistration.StepFour";
export default StepFour;
